import "./ItemizedBillOverview.scss";

import { keepPreviousData } from "@tanstack/react-query";
import { useAtom } from "jotai";
import {
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";

import { Close, Download } from "@/assets/icons/ds";
import { SearchIcon } from "@/assets/icons/icons";
import { Button } from "@/components/Interface/Button/Button";
import { ListBoxMultiple } from "@/components/Interface/FormFields/ListBoxMultiple/ListBoxMultiple";
import { Pagination } from "@/components/Interface/Pagination/Pagination";
import { Pill } from "@/components/Interface/Pill/Pill";
import { LoadingSpinner } from "@/components/Layout/LoadingSpinner/LoadingSpinner";
import { PageNumberComboBox } from "@/components/Layout/Table/PageNumberComboBox/PageNumberComboBox";
import {
  clampPage,
  clampPageSize,
} from "@/components/Layout/Table/PageNumberComboBox/pageSize";
import { Table } from "@/components/Layout/Table/Table";
import { TableControlGroup } from "@/components/Layout/Table/TableControlGroup/TableControlGroup";
import { TableEmptyState } from "@/components/Layout/Table/TableEmptyState/TableEmptyState";
import { useBodyScrollLock } from "@/hooks/useBodyScrollLock";
import { useHandleError } from "@/hooks/useHandleError";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useQueryCallbacks } from "@/hooks/useQueryCallbacks";
import { useQueryParam } from "@/hooks/useQueryParam";
import {
  getEvnFiles,
  useGetDevices as useGetEvnDevices,
  useGetEvnForCustomer,
} from "@/services/api";
import {
  GetEvnForCustomerSortDirection,
  GetEvnForCustomerSortField,
} from "@/services/model";
import { documentsEditStateAtom } from "@/utils/atoms";
import { downloadDocument } from "@/utils/downloadDocument";

import { EvnSelectAllCheckbox } from "./EvnSelectAllCheckbox";
import { ItemizedBillOverviewHeader } from "./ItemizedBillOverviewHeader";
import { ItemizedBillOverviewRow } from "./ItemizedBillOverviewRow";

/**
 * TODO: File is way too large, split it up into multiple files
 */
export type ItemizedBillListIdForm = {
  selectedItems: string[]; // stores encoded values
};

type FilterFormDevice = {
  deviceList: string[];
};

type FilterFormPeriod = {
  periods: string[];
};

const sortFields: string[] = Object.values(GetEvnForCustomerSortField);

/** Headless UI focuses `Listbox.Options` on open; refocus this field and stop keys bubbling to listbox typeahead. */
const EvnDeviceLineComboHeader = ({
  open,
  deviceComboFilter,
  setDeviceComboFilter,
  inputRef,
}: {
  open: boolean;
  deviceComboFilter: string;
  setDeviceComboFilter: (value: string) => void;
  inputRef: RefObject<HTMLInputElement | null>;
}) => {
  const { t } = useTranslation();

  useLayoutEffect(() => {
    if (!open) {
      return;
    }
    const id = window.setTimeout(() => {
      inputRef.current?.focus({ preventScroll: true });
    }, 0);

    return () => window.clearTimeout(id);
  }, [open, inputRef]);

  const clearFilter = () => {
    setDeviceComboFilter("");
    window.queueMicrotask(() => {
      inputRef.current?.focus({ preventScroll: true });
    });
  };

  return (
    // Figma's "select" spec for this row: h-14 (64px), translucent white
    // fill (surface-tertiary-inverse) on the dark flyout background, white
    // text — not the flyout's own solid bg, and not the plain white input
    // row the pre-redesign combo filter used.
    // Horizontal padding is px-3, not Figma's literal px-5 for this row: the
    // "select" and "dire-option" components use different padding tokens
    // (20px vs 12px) in the source file, which puts this row's search icon
    // and each option row's checkmark at different right-edge insets — a
    // deliberate deviation to line the search icon up with the checkmarks.
    <div
      className="mb-1 flex h-14 items-center gap-3 rounded-md bg-surface-tertiary-inverse px-3"
      onMouseDown={(e) => e.preventDefault()}
    >
      <label className="sr-only" htmlFor="evn-device-combo-filter">
        {t("documents.tabItemizedBill.placeholderSearchTerm")}
      </label>
      <input
        id="evn-device-combo-filter"
        data-testid="evn-device-combo-filter-input"
        className="min-h-12 min-w-0 flex-1 border-0 bg-transparent px-4 py-1.5 text-body-base text-foreground-primary-inverse outline-none ring-0 placeholder:text-foreground-tertiary-inverse"
        ref={inputRef}
        type="text"
        inputMode="search"
        autoComplete="off"
        value={deviceComboFilter}
        onChange={(e) => setDeviceComboFilter(e.target.value)}
        onKeyDown={(e) => e.stopPropagation()}
        placeholder={t("documents.tabItemizedBill.placeholderSearchTerm")}
      />
      {deviceComboFilter ? (
        <button
          className="flex shrink-0 items-center justify-center rounded p-1 text-foreground-primary-inverse hover:bg-surface-tertiary-inverse focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-foreground-primary-inverse"
          type="button"
          aria-label={t("portal:common.buttons.clearSearch")}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onClick={(e) => {
            e.stopPropagation();
            clearFilter();
          }}
        >
          <Close className="h-4 w-4" aria-hidden="true" />
        </button>
      ) : (
        <SearchIcon
          className="size-6 shrink-0 text-foreground-primary-inverse"
          aria-hidden="true"
        />
      )}
    </div>
  );
};

/**
 * Selected-device pills shown inside the "Phone number" select, matching Figma's
 * "dire-tag" chips: the first two selections render as removable tags, any
 * remainder collapses into a trailing "+n" tag. Clicks on a tag's remove
 * button must not toggle the listbox open/closed, hence the guarded
 * mousedown/click pair also used by EvnDeviceLineComboHeader's clear button.
 */
const MAX_VISIBLE_DEVICE_TAGS = 2;

/**
 * Only renders while the trigger is closed — while `open`, or with nothing
 * selected, it renders ListBoxMultiple's own default collapsed text instead,
 * matching Figma (the open trigger shows plain text, tags only appear once
 * it's closed again).
 */
const SelectedDeviceTags = ({
  selected,
  setSelected,
  open,
  defaultValue,
}: {
  selected: (string | undefined)[] | undefined;
  setSelected: (value: (string | undefined)[]) => void;
  open: boolean;
  defaultValue: ReactNode;
}) => {
  const { t } = useTranslation();
  const values = selected ?? [];

  if (open || values.length === 0) {
    return defaultValue;
  }

  const visible = values.slice(0, MAX_VISIBLE_DEVICE_TAGS);
  const overflowCount = values.length - visible.length;

  const tagClassName = "bg-surface-primary text-foreground-primary-inverse";

  return (
    // Single line, like Figma's tag group: it does not wrap to a second row
    // when tags don't fit, it just clips (the "+n" overflow tag exists
    // precisely so that clipping isn't the common case).
    <div className="flex items-center gap-1 overflow-hidden">
      {visible.map((value) => (
        <Pill
          key={value}
          className={tagClassName}
          size="tag"
          text={value ?? ""}
          iconPosition="trailing"
          icon={
            /* A pointer-only shortcut, hidden from assistive tech: this whole
               tag row renders inside Headless UI's `Listbox.Button`, and a
               nested control there is invalid (a `<button>` breaks DOM
               nesting, a `role="button"` span nests interactive roles).
               Keyboard and screen-reader users remove the number by toggling
               the entry off in the open dropdown. */
            <span
              className="flex shrink-0 cursor-pointer items-center justify-center"
              aria-hidden
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.stopPropagation();
                setSelected(values.filter((v) => v !== value));
              }}
            >
              <Close className="size-3.5" aria-hidden="true" />
            </span>
          }
        />
      ))}
      {overflowCount > 0 && (
        <Pill
          className={tagClassName}
          size="tag"
          text={`+${overflowCount}`}
          ariaLabel={t("documents.tabItemizedBill.additionalSelectedNumbers", {
            count: overflowCount,
          })}
        />
      )}
    </div>
  );
};

export const ItemizedBillOverview = () => {
  const { t } = useTranslation();
  const handleError = useHandleError();
  const isMobile = useIsMobile();
  const [searchParams] = useSearchParams();

  const [shouldShowEntries, setShouldShowEntries] = useState(false);
  const [isFetchingFile, setIsFetchingFile] = useState(false);
  const [shouldFetch, setShouldFetch] = useState(true);
  const [deviceComboFilter, setDeviceComboFilter] = useState("");
  const evnDeviceComboInputRef = useRef<HTMLInputElement>(null);
  const refocusEvnDeviceComboFilter = useCallback(() => {
    window.queueMicrotask(() => {
      evnDeviceComboInputRef.current?.focus({ preventScroll: true });
    });
  }, []);
  const [availablePeriods, setAvailablePeriods] =
    useState<(string | undefined)[]>();
  const [documentsEdit, setDocumentsEdit] = useAtom(documentsEditStateAtom);
  const isSelectMultiple = documentsEdit.itemizedBillsSelecting;
  const setIsSelectMultiple = (selecting: boolean) => {
    setDocumentsEdit((prev) => ({
      ...prev,
      itemizedBillsSelecting: selecting,
    }));
  };
  useBodyScrollLock(isSelectMultiple && isMobile);

  const currentSortField = searchParams.get("sortField");
  const typedCurrentSortField = sortFields.includes(`${currentSortField}`)
    ? (currentSortField as GetEvnForCustomerSortField)
    : undefined;
  const currentSortDirection = searchParams.get("sortDirection");
  const typedCurrentSortDirection =
    currentSortDirection === "asc"
      ? GetEvnForCustomerSortDirection.asc
      : currentSortDirection === "desc"
        ? GetEvnForCustomerSortDirection.desc
        : undefined;

  const { watch: watchDevice, control: controlDevice } =
    useForm<FilterFormDevice>({
      defaultValues: {},
    });
  const formStateDevice = watchDevice();

  const { watch: watchPeriod, control: controlPeriod } =
    useForm<FilterFormPeriod>({
      defaultValues: {},
    });
  const formStatePeriod = watchPeriod();
  const hasSelectedDeviceFilter =
    (formStateDevice?.deviceList?.length ?? 0) > 0;
  const hasSelectedPeriodFilter = (formStatePeriod?.periods?.length ?? 0) > 0;

  const { data: itemizedBillDevices, isFetching: isFetchingDevices } =
    useGetEvnDevices();

  const filteredPlanDeviceLabels = useMemo(() => {
    const labels = (itemizedBillDevices ?? [])
      .map((d) => d.device)
      .filter((d): d is string => Boolean(d));
    const q = deviceComboFilter.trim().toLowerCase().replace(/\s+/g, "");

    if (!q) {
      return labels;
    }

    return labels.filter((label) =>
      label.toLowerCase().replace(/\s+/g, "").includes(q),
    );
  }, [itemizedBillDevices, deviceComboFilter]);

  const [itemizedBillPage, setCurrentItemizedBillPage] = useQueryParam("page");
  const currentItemizedBillPage = clampPage(itemizedBillPage || null);
  const [pageSizeParam] = useQueryParam("pageSize");
  const currentPageSize = clampPageSize(pageSizeParam || null);

  /** Contract text search is not exposed in UI; expression matches previous `searchTerm?.trim() || undefined` when empty. */
  const contractSearchForApi = "";

  const evnForCustomerQuery = useGetEvnForCustomer(
    {
      searchTermContract: contractSearchForApi.trim() || undefined,
      periods: formStatePeriod.periods,
      page: currentItemizedBillPage - 1,
      pageSize: currentPageSize,
      sortField: typedCurrentSortField,
      sortDirection: typedCurrentSortDirection,
    },
    {
      query: {
        refetchOnWindowFocus: false,
        placeholderData: keepPreviousData,
        enabled: shouldFetch,
      },
    },
  );
  useQueryCallbacks(evnForCustomerQuery, {
    onSuccess: (data) => {
      setShouldFetch(false);

      setAvailablePeriods((prev) => [
        ...new Set([
          ...(prev ?? []),
          ...(data?.evns?.map((evn) => evn.periode) ?? []),
        ]),
      ]);

      // If the total number of pages is smaller than the current selected page,
      // (e.g. we are on page 3 but the data has only 2 pages now)
      // reset to the next possible value
      if (
        data.totalNumberPages &&
        data.totalNumberPages < currentItemizedBillPage
      ) {
        setCurrentItemizedBillPage(data.totalNumberPages.toString());
      }
    },
  });
  const { data: itemizedBillOverview, isFetching: isFetchingOverview } =
    evnForCustomerQuery;

  useEffect(() => {
    if (shouldShowEntries) {
      setShouldFetch(true);
    }
  }, [
    currentItemizedBillPage,
    currentPageSize,
    typedCurrentSortField,
    typedCurrentSortDirection,
    shouldShowEntries,
  ]);

  const hasItemizedBills = useMemo(
    () => itemizedBillOverview?.evns && itemizedBillOverview?.evns?.length >= 1,
    [itemizedBillOverview?.evns],
  );

  const requestedItemizedBills = useMemo(() => {
    if (shouldShowEntries && !shouldFetch) {
      if (formStateDevice.deviceList && formStateDevice.deviceList.length > 0) {
        return (
          itemizedBillOverview?.evns?.filter((evn) =>
            formStateDevice.deviceList.includes(evn.contract || ""),
          ) || []
        );
      }

      return itemizedBillOverview?.evns ?? [];
    }

    return [];
  }, [
    formStateDevice.deviceList,
    shouldShowEntries,
    shouldFetch,
    itemizedBillOverview?.evns,
  ]);

  const formMethods = useForm<ItemizedBillListIdForm>({
    defaultValues: { selectedItems: [] },
  });

  const selectedValues = formMethods.watch("selectedItems") ?? [];

  /* Changing a filter produces a new result set, so a selection carried over
     from the previous one is no longer visible anywhere in the table — the
     count button would keep counting rows the user cannot see, and the
     download would hand them those hidden bills. Selection deliberately does
     survive paging (like the bills list), which is why this keys off the
     filters and not off the current page's ids. */
  const activeFilterKey = [
    ...(formStateDevice.deviceList ?? []),
    "|",
    ...(formStatePeriod.periods ?? []),
  ].join(",");

  const { resetField } = formMethods;

  useEffect(() => {
    resetField("selectedItems");
  }, [activeFilterKey, resetField]);

  const selectedItems = selectedValues.map((value) => {
    const [serviceId, period] = value.split("|");

    return {
      serviceId: Number(serviceId),
      period,
    };
  });

  // One period comes back as a PDF, several as a ZIP.
  const evnDownloadOptions = {
    pdfFilenamePrefix: "DR_EVN",
    zipName: t("documents.tabItemizedBill.zipName"),
  };

  const downloadItemizedBill = ({
    serviceId,
    period,
  }: {
    serviceId: number;
    period: string;
  }) => {
    setIsFetchingFile(true);

    getEvnFiles({ serviceIds: [serviceId], periods: [period] })
      .then((data) => downloadDocument(data.evnFile, evnDownloadOptions))
      .catch((error) => handleError(error))
      .finally(() => setIsFetchingFile(false));
  };

  const downloadSelectedItemizedBills = () => {
    setIsFetchingFile(true);

    getEvnFiles({
      serviceIds: selectedItems.map((item) => item.serviceId),
      periods: selectedItems.map((item) => item.period),
    })
      .then((data) => downloadDocument(data.evnFile, evnDownloadOptions))
      .catch((error) => handleError(error))
      .finally(() => setIsFetchingFile(false));
  };

  const itemizedBillPages = itemizedBillOverview?.totalNumberPages || 1;

  const currentPageItemizedBillIds = useMemo(
    () =>
      requestedItemizedBills
        ?.filter(
          (itemizedBill) => itemizedBill.serviceId && itemizedBill.periode,
        )
        .map(
          (itemizedBill) => `${itemizedBill.serviceId}|${itemizedBill.periode}`,
        ) ?? [],
    [requestedItemizedBills],
  );

  const hasResults = requestedItemizedBills.length > 0;

  const leaveSelection = () => {
    formMethods.resetField("selectedItems");
    setIsSelectMultiple(false);
  };

  /**
   * Figma's empty state is a single centred hint line under the results
   * header — no illustration, and no "show all" button: the header's own
   * "Search" already runs an unfiltered search, which is all that button did.
   */
  /* Plain JSX, not inline components: a new component identity on every
     render makes React unmount and remount the whole subtree on each state
     change, which drops focus from the device filter input mid-typing and
     resets the rows. Same rule the bills list follows. */
  const info = (
    <TableEmptyState
      message={
        hasItemizedBills
          ? t("documents.tabItemizedBill.choosePlanAndPeriod")
          : t("documents.tabItemizedBill.noItemizedBillOverview")
      }
    />
  );

  const mobileView = (
    <>
      <ul className="flex flex-col">
        {requestedItemizedBills?.map((itemizedBill) => (
          <li key={itemizedBill.id}>
            <ItemizedBillOverviewRow
              itemizedBill={itemizedBill}
              variant="compact"
              isSelectMultiple={isSelectMultiple}
              downloadItemizedBill={downloadItemizedBill}
            />
          </li>
        ))}
      </ul>
      {shouldShowEntries && (
        <TableControlGroup>
          {/* Same stacked footer the bills list uses on mobile: rows-per-page
              first, then the full-width pagination row. */}
          <div className="flex w-full flex-col gap-4">
            <PageNumberComboBox />
            {itemizedBillPages > 1 && (
              <Pagination paginationPages={itemizedBillPages} />
            )}
          </div>
        </TableControlGroup>
      )}
    </>
  );

  const desktopView = (
    <>
      <Table
        variant="plain"
        hasPaddingBottom={false}
        isLoading={isFetchingOverview}
      >
        <thead>
          <ItemizedBillOverviewHeader
            currentPageItemizedBillIds={currentPageItemizedBillIds}
            isSelecting={isSelectMultiple}
          />
        </thead>
        <tbody>
          {requestedItemizedBills?.map((itemizedBill) => (
            <ItemizedBillOverviewRow
              key={itemizedBill.id}
              itemizedBill={itemizedBill}
              isSelectMultiple={isSelectMultiple}
              downloadItemizedBill={downloadItemizedBill}
            />
          ))}
        </tbody>
      </Table>
      <div className="flex w-full flex-row items-center justify-between px-4 py-2">
        <PageNumberComboBox />
        {itemizedBillPages > 1 && (
          <Pagination paginationPages={itemizedBillPages} />
        )}
      </div>
    </>
  );

  /* Three states per Figma. With no results the row still renders, with a
     disabled "0 herunterladen"; once there are results a single "Mehrere
     herunterladen" arms multi-select, and armed it becomes "Abbrechen" plus
     the counted download — the pattern the bills list uses for its own table. */
  const headerActions = !hasResults ? (
    <Button
      dataTestid="evn-download-selected"
      variant="secondary"
      size="sm"
      icon={<Download />}
      disabled
    >
      {t("bills.download", { count: 0 })}
    </Button>
  ) : isSelectMultiple ? (
    <div className="flex shrink-0 items-center gap-3">
      <Button variant="secondary" size="sm" onClick={leaveSelection}>
        {t("Common.label.cancel")}
      </Button>
      {/* Stays filled at a count of zero, so the click is guarded instead of
          the button being disabled. */}
      <Button
        dataTestid="evn-download-selected"
        variant="primary"
        size="sm"
        icon={<Download />}
        ariaLabel={t("bills.download", { count: selectedValues.length })}
        loading={isFetchingFile}
        onClick={() => {
          if (selectedValues.length > 0) {
            downloadSelectedItemizedBills();
          }
        }}
      >
        {`${selectedValues.length}`}
      </Button>
    </div>
  ) : (
    <Button
      dataTestid="evn-select-multiple"
      variant="primary"
      size="sm"
      icon={<Download />}
      onClick={() => setIsSelectMultiple(true)}
    >
      {t("bills.downloadMultiple")}
    </Button>
  );

  return (
    <div className="evn-list">
      {(isFetchingDevices || isFetchingOverview || isFetchingFile) && (
        <LoadingSpinner />
      )}
      <div
        className={`evn-list__card flex flex-col rounded-lg bg-surface-accent-ivory p-4 ${
          isMobile && isSelectMultiple ? "evn-list__card--pinned" : ""
        }`}
      >
        {!(isMobile && isSelectMultiple) && (
          <>
            <div className="flex items-start gap-3">
              <div className="min-w-0 flex-1 px-2 py-2">
                <h2 className="text-body-base font-semibold leading-none text-foreground-primary">
                  {t("documents.tabItemizedBill.headline")}
                </h2>
                <p className="mt-2 text-body-sm text-foreground-secondary">
                  {t("documents.tabItemizedBill.subtitle")}
                </p>
              </div>
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  setShouldFetch(true);
                  setShouldShowEntries(true);
                  setCurrentItemizedBillPage(undefined);
                }}
              >
                {t("documents.tabItemizedBill.btnSearch")}
              </Button>
            </div>

            <div className="mt-gutter flex w-full flex-col gap-2 tablet:flex-row">
              <div className="flex w-full flex-col">
                <Controller<FilterFormDevice>
                  control={controlDevice}
                  name="deviceList"
                  render={({ field: { onChange, value } }) => (
                    <ListBoxMultiple<string | undefined>
                      className="full-width"
                      label={t("portal:common.phoneNumber")}
                      itemLabel={t("portal:common.phoneNumber")}
                      placeholder={t(
                        hasSelectedPeriodFilter
                          ? "documents.tabItemizedBill.onlyOneFilterAllowed"
                          : "documents.tabItemizedBill.placeholderPlans",
                      )}
                      selected={typeof value === "string" ? [value] : value}
                      setSelected={onChange}
                      displayValue={(v) => v ?? ""}
                      availableValues={filteredPlanDeviceLabels}
                      disabled={
                        hasSelectedPeriodFilter || !itemizedBillDevices?.length
                      }
                      hideArrowWhenDisabled={hasSelectedPeriodFilter}
                      showAmountForMultipleSelection
                      afterSelectionChange={refocusEvnDeviceComboFilter}
                      renderSelectedValue={(
                        value,
                        setValue,
                        open,
                        defaultValue,
                      ) => (
                        <SelectedDeviceTags
                          selected={value}
                          setSelected={setValue}
                          open={open}
                          defaultValue={defaultValue}
                        />
                      )}
                      optionsHeader={({ open }) => (
                        <EvnDeviceLineComboHeader
                          open={open}
                          deviceComboFilter={deviceComboFilter}
                          setDeviceComboFilter={setDeviceComboFilter}
                          inputRef={evnDeviceComboInputRef}
                        />
                      )}
                    />
                  )}
                />
              </div>

              <div className="flex w-full flex-col">
                <Controller<FilterFormPeriod>
                  control={controlPeriod}
                  name="periods"
                  render={({ field: { onChange, value } }) => (
                    <ListBoxMultiple<string | undefined>
                      className="full-width"
                      label={t("Common.label.period")}
                      itemLabel={t("Content.periods")}
                      placeholder={t(
                        hasSelectedDeviceFilter
                          ? "documents.tabItemizedBill.onlyOneFilterAllowed"
                          : "documents.tabItemizedBill.placeholderPeriods",
                      )}
                      selected={typeof value === "string" ? [value] : value}
                      setSelected={onChange}
                      displayValue={(value) => value ?? ""}
                      availableValues={availablePeriods || []}
                      disabled={
                        hasSelectedDeviceFilter ||
                        !availablePeriods ||
                        availablePeriods.length <= 0
                      }
                      hideArrowWhenDisabled={hasSelectedDeviceFilter}
                      showAmountForMultipleSelection
                    />
                  )}
                />
              </div>
            </div>
          </>
        )}

        <FormProvider {...formMethods}>
          {/* The results header is part of the card in every state, including
              before the first search — per Figma, where the empty state still
              shows it above the hint line. */}
          <div
            className={`evn-list__header flex shrink-0 items-center gap-3 ${
              isMobile && isSelectMultiple ? "mt-0" : "mt-gutter"
            } ${
              // Figma's 40px above the hint line
              hasResults ? "mb-5" : "mb-4"
            }`}
          >
            {/* Mobile has no table header to hang the select-all off, so it
                sits beside the results heading instead. */}
            {isMobile && isSelectMultiple && (
              <EvnSelectAllCheckbox
                currentPageItemizedBillIds={currentPageItemizedBillIds}
              />
            )}
            <div className="min-w-0 flex-1 px-2">
              <h3 className="truncate py-2 text-body-base font-semibold leading-none text-foreground-primary">
                {t("documents.tabItemizedBill.resultsHeadline")}
              </h3>
            </div>
            {headerActions}
          </div>
          <div className="evn-list__body">
            {hasResults ? (
              <div className="flex flex-col">
                {isMobile ? mobileView : desktopView}
              </div>
            ) : (
              <div className="flex flex-col items-center text-center">
                {info}
              </div>
            )}
          </div>
        </FormProvider>
      </div>
    </div>
  );
};
